import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const useStaleInterviewCheck = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [staleInterviews, setStaleInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const checkForStaleInterviews = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setIsLoading(true);

    try {
      console.log('Checking for stale interviews...');
      const response = await axios.get('/api/interviews/checkStaleInterviews', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const staleInterviewsData = response.data.staleInterviews || [];
      console.log(`Found ${staleInterviewsData.length} stale interviews`);
      
      if (staleInterviewsData.length > 0) {
        console.log(`Found ${staleInterviewsData.length} stale interviews, showing popup...`);
        
        // Always show popup if there are stale interviews - no dismissal logic
        console.log('Showing stale interview popup with interviews:', staleInterviewsData.map(r => r.fields?.['Role Title (from Role)'] || 'No Title'));
        
        // Transform the data to match the expected format - using the correct field names from in_process.js
        const transformedInterviews = staleInterviewsData.map(record => ({
          id: record.id,
          recordId: record.id,
          stage: record.fields.Stage,
          'Role Title': record.fields['Role Title (from Role)'] || 'No Title',
          'Name (from Startup)': record.fields['Name (from Startup) (from Role)'] || 'Unknown Company',
          'Candidate Interview Stage': record.fields['Candidate Interview Stage'],
          'Candidate Stage (last modified)': record.fields['Candidate Stage (last modified)'],
          'Last Updated': record.fields['Last Updated'], // Add our custom timestamp field
          // Add additional fields that might be useful
          'URL ID': record.fields['URL ID (from Role)'] || '',
          'Location (from Startup)': record.fields['Location (from Startup) (from Role)'] || '',
          'Compensation': record.fields['Compensation (from Role)'] || '',
          'Funding Stage (from Startup)': record.fields['Funding Stage (from Startup) (from Role)'] || '',
          'Team Size (from Startup)': record.fields['Team Size (from Startup) (from Role)'] || ''
        }));
        
        console.log('Transformed interviews for popup:', transformedInterviews);
        setStaleInterviews(transformedInterviews);
        setShowPopup(true);
      } else {
        console.log('No stale interviews found, not showing popup');
      }

      setHasChecked(true);
    } catch (error) {
      console.error('Error checking for stale interviews:', error);
      // Don't show error to user, just log it
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Trigger check on mount if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('StaleInterviewCheck effect running:', { hasToken: !!token });
    
    if (token) {
      console.log('Setting up stale interview check timer...');
      // Small delay to allow other components to load first
      const timer = setTimeout(() => {
        console.log('Timer fired, checking for stale interviews...');
        checkForStaleInterviews();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [checkForStaleInterviews]);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
    setStaleInterviews([]);
  }, []);

  const triggerManualCheck = useCallback(() => {
    checkForStaleInterviews();
  }, [checkForStaleInterviews]);

  return {
    showPopup,
    staleInterviews,
    isLoading,
    hasChecked,
    handleClosePopup,
    triggerManualCheck,
    checkForStaleInterviews
  };
}; 